import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, CheckCircle, Clock, MagnifyingGlass, CalendarBlank, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";

const KAT_COLORS = { tebar: "bg-blue-100 text-blue-700", monitoring: "bg-emerald-100 text-emerald-700", sampling: "bg-purple-100 text-purple-700", panen: "bg-amber-100 text-amber-700", evaluasi: "bg-slate-100 text-slate-700" };
const STATUS_COLORS = { selesai: "bg-emerald-100 text-emerald-700", belum: "bg-slate-100 text-slate-600", terlambat: "bg-red-100 text-red-700" };

export default function JadwalPage() {
  const [items, setItems] = useState([]);
  const [siklusList, setSiklusList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filterSiklus, setFilterSiklus] = useState("__all__");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ siklus_id: "", tanggal: "", judul: "", kategori: "monitoring", keterangan: "" });

  const load = useCallback(async () => {
    const sq = filterSiklus !== "__all__" ? `&siklus_id=${filterSiklus}` : "";
    const [j, s] = await Promise.all([
      api.get(`/jadwal?page=${page}&limit=20&search=${search}${sq}`),
      api.get("/siklus?limit=500")
    ]);
    setItems(j.data.data);
    setTotal(j.data.total);
    setSiklusList(s.data.data);
  }, [page, search, filterSiklus]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try { await api.post("/jadwal", form); toast.success("Jadwal ditambahkan"); setDialogOpen(false); load(); } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const toggleStatus = async (item) => {
    const newStatus = item.status === "selesai" ? "belum" : "selesai";
    await api.put(`/jadwal/${item._id}`, { status: newStatus });
    load();
  };

  const generateForSiklus = async (siklusId) => {
    try {
      const { data } = await api.post(`/siklus/${siklusId}/generate-jadwal`);
      toast.success(data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal"); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="jadwal-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Jadwal Kegiatan</h1>
          <p className="text-sm text-slate-500 mt-1">Kalender kegiatan per siklus budidaya</p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild><Button data-testid="create-jadwal-btn" className="bg-[#B8860B] hover:bg-[#9A7209] text-white"><Plus size={18} className="mr-2" /> Jadwal Baru</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>Jadwal Baru</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Siklus</Label>
                  <Select value={form.siklus_id} onValueChange={(v) => setForm({ ...form, siklus_id: v })}><SelectTrigger><SelectValue placeholder="Pilih Siklus" /></SelectTrigger><SelectContent>{siklusList.map(s => <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal</Label><Input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kategori</Label>
                    <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monitoring">Monitoring</SelectItem><SelectItem value="sampling">Sampling</SelectItem><SelectItem value="panen">Panen</SelectItem><SelectItem value="evaluasi">Evaluasi</SelectItem><SelectItem value="tebar">Tebar</SelectItem></SelectContent></Select></div>
                </div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Judul</Label><Input value={form.judul} onChange={e => setForm({ ...form, judul: e.target.value })} required /></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Keterangan</Label><Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} /></div>
                <Button type="submit" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-64"><MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input placeholder="Cari jadwal..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" /></div>
        <Select value={filterSiklus} onValueChange={v => { setFilterSiklus(v); setPage(1); }}>
          <SelectTrigger className="w-[240px] h-9"><SelectValue placeholder="Semua Siklus" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">Semua Siklus</SelectItem>{siklusList.map(s => <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>)}</SelectContent>
        </Select>
        {filterSiklus !== "__all__" && (
          <Button size="sm" variant="outline" onClick={() => generateForSiklus(filterSiklus)} className="border-[#008080] text-[#008080]"><Lightning size={14} className="mr-1" /> Auto-Generate</Button>
        )}
      </div>

      {/* Timeline cards */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <Card className="bg-white border border-slate-200"><CardContent className="py-12 text-center text-slate-400 text-sm">Belum ada jadwal. Pilih siklus lalu klik "Auto-Generate" untuk membuat jadwal otomatis.</CardContent></Card>
        ) : items.map(item => {
          const isPast = item.tanggal < today && item.status !== "selesai";
          return (
            <Card key={item._id} className={`bg-white border transition-all hover:shadow-sm ${isPast ? "border-red-200" : item.status === "selesai" ? "border-emerald-200" : "border-slate-200"}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <button onClick={() => toggleStatus(item)} className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 transition-all ${item.status === "selesai" ? "bg-emerald-500 border-emerald-500 text-white" : isPast ? "border-red-300 text-red-300 hover:bg-red-50" : "border-slate-300 text-slate-300 hover:bg-slate-50"}`}>
                  <CheckCircle size={18} weight={item.status === "selesai" ? "fill" : "regular"} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-medium ${item.status === "selesai" ? "line-through text-slate-400" : "text-[#1A1A1A]"}`}>{item.judul}</p>
                    <Badge className={`text-[10px] h-5 ${KAT_COLORS[item.kategori] || "bg-slate-100"} hover:${KAT_COLORS[item.kategori] || ""}`}>{item.kategori}</Badge>
                    {isPast && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px] h-5">Terlambat</Badge>}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{item.tambak_nama} {item.doc_hari != null ? `• DOC ${item.doc_hari}` : ""} {item.keterangan ? `• ${item.keterangan}` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium text-slate-600">{item.tanggal}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Pagination page={page} limit={20} total={total} onPageChange={setPage} />
    </div>
  );
}
